import React, { useEffect, useState } from "react";
import {
  createStyles,
  Theme,
  WithStyles,
  Typography,
  Button,
  Box,
} from "@material-ui/core";
import withStyles from "@material-ui/core/styles/withStyles";
import { CheckCircle } from "@material-ui/icons";
import { connect } from "react-redux";
import { hideDialog, showDialog } from "components/SharedComponents/Dialog";
import GameNavigationDialog from "components/Game/GameNavigationDialog";
import GameActions from "../../actions/games";
import paymentApi from "../../api/payment";

interface Props extends WithStyles<typeof styles> {
  gameId?: string;
  sessionId?: string;
  history?: any;
  getGameById: (id: string, queryType?: string, updateRedux?: boolean, callback?: any) => void;
}

const StripePaymentSuccess: React.FC<Props> = ({
  classes,
  gameId,
  sessionId,
  getGameById,
  history,
}) => {
  const [confirmed, setConfirmed] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [confirmError, setConfirmError] = useState<string | null>(null);

  const confirmPayment = () => {
    if (!sessionId) return;
    setConfirming(true);
    setConfirmError(null);
    paymentApi
      .confirmStripeSession(sessionId)
      .then(() => setConfirmed(true))
      .catch((err: any) => {
        const message = err?.response?.data?.error || "We could not verify payment yet. Please retry.";
        setConfirmError(message);
      })
      .finally(() => setConfirming(false));
  };

  useEffect(() => {
    if (sessionId) {
      confirmPayment();
    }
    // sessionId is the only dependency; confirmPayment is intentionally recreated per render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  const handleProceed = () => {
    hideDialog();
    if (history) history.replace("/");
    if (gameId) {
      getGameById(gameId, undefined, true);
      setTimeout(() => {
        showDialog(<GameNavigationDialog gameId={gameId} />, "xs", true, true, true, "Game menu");
      }, 500);
    }
  };

  return (
    <Box className={classes.page}>
      <Box className={classes.card}>
        <Box className={classes.iconWrap}>
          <CheckCircle className={classes.icon} />
        </Box>
        <Typography variant="h5" component="h1" className={classes.title}>
          Payment Successful!
        </Typography>
        <Typography variant="body1" className={classes.body} color="textSecondary">
          {confirmError
            ? "Your payment may have gone through, but we could not verify it yet."
            : confirmed
              ? "Your payment has been recorded. You can proceed to the game."
              : "Your payment has been received. It may take a few moments to update in the game."}
        </Typography>
        {confirmError ? (
          <Typography variant="body2" className={classes.errorText}>
            {confirmError}
          </Typography>
        ) : null}
        <Typography variant="body2" className={classes.footer} color="textSecondary">
          Thank you for using Schala.
        </Typography>
        {confirmError ? (
          <Button
            variant="outlined"
            color="primary"
            onClick={confirmPayment}
            className={classes.retryButton}
            fullWidth
            disabled={confirming}
          >
            {confirming ? "Retrying..." : "Retry Confirmation"}
          </Button>
        ) : null}
        <Button
          variant="contained"
          color="primary"
          onClick={handleProceed}
          className={classes.button}
          fullWidth
          size="large"
          disabled={!!confirmError || confirming}
        >
          {gameId ? "Proceed to Game" : "Back to Dashboard"}
        </Button>
      </Box>
    </Box>
  );
};

const styles = (theme: Theme) =>
  createStyles({
    page: {
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      minHeight: "calc(100vh - 56px)",
      padding: theme.spacing(2),
      boxSizing: "border-box",
      width: "100%",
    },
    card: {
      width: "100%",
      maxWidth: 420,
      padding: theme.spacing(4),
      backgroundColor: theme.palette.background.paper,
      boxShadow: "0 8px 40px rgba(46, 125, 50, 0.12)",
      borderRadius: 16,
      textAlign: "center",
      border: "1px solid rgba(46, 125, 50, 0.15)",
      animation: "$slideUp 0.4s ease-out",
      [theme.breakpoints.up("sm")]: {
        padding: theme.spacing(5),
      },
    },
    "@keyframes slideUp": {
      from: {
        opacity: 0,
        transform: "translateY(16px)",
      },
      to: {
        opacity: 1,
        transform: "translateY(0)",
      },
    },
    iconWrap: {
      display: "flex",
      justifyContent: "center",
      marginBottom: theme.spacing(3),
    },
    icon: {
      fontSize: 72,
      color: "#2e7d32",
      filter: "drop-shadow(0 4px 12px rgba(46, 125, 50, 0.25))",
    },
    title: {
      fontWeight: 700,
      color: "#1b5e20",
      marginBottom: theme.spacing(2),
      letterSpacing: "-0.02em",
    },
    body: {
      marginBottom: theme.spacing(2),
      lineHeight: 1.6,
    },
    errorText: {
      marginBottom: theme.spacing(2),
      color: theme.palette.error.main,
    },
    footer: {
      marginBottom: theme.spacing(3),
    },
    retryButton: {
      marginBottom: theme.spacing(1.5),
      textTransform: "none",
      fontWeight: 600,
    },
    button: {
      marginTop: theme.spacing(1),
      padding: theme.spacing(1.5, 3),
      borderRadius: 12,
      fontWeight: 700,
      textTransform: "none",
      fontSize: "1rem",
    },
  });

const mapStateToProps = (_state: any, ownProps: any) => {
  const params = new URLSearchParams(ownProps.location?.search || "");
  return {
    gameId: params.get("game_id") || undefined,
    sessionId: params.get("session_id") || undefined,
  };
};

export default connect(mapStateToProps, {
  getGameById: GameActions.getById,
})(withStyles(styles)(StripePaymentSuccess));
